import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ModalConfirmationComponent } from 'src/app/components/modal-confirmation/modal-confirmation.component';
import { ValidateTwoValueInString, ValidateValueInString } from 'src/app/helpers/Validators/specific-value-in-string-validator';
import { OperationTransferenceService } from 'src/app/services/operation/operation-transference.service';
import { catchError, debounceTime, distinctUntilChanged, filter, of, startWith, Subject, switchMap, takeUntil } from 'rxjs';
import { CpfCnpjValidator } from 'src/app/helpers/Validators/CpfCnpjValidator/cpf-cnpj.validator';
import { CarbonOperationType } from 'src/app/shared/interfaces/carbon-operation-type.enum';
import { CarbonOperation } from 'src/app/shared/interfaces/carbon-operation';
import { AppModalService } from 'src/app/shared/services/app-modal.service';
import {
  WithdrawalModalAction,
  WithdrawalResultModalViewModel,
  buildWithdrawalResultModalViewModel,
} from './modals/modal-success-retirement/withdrawal-result-modal.model';
import { Router } from '@angular/router';
import { comitenteBackendValidator } from 'src/app/helpers/Validators/ComitenteValidatorBackend/comitente-backend.validator';
import {
  extractDocumentCharacters,
  formatCNPJProgressive,
  formatCpfCnpjInputValue,
} from 'src/app/helpers/utils/cpf-cnpj.util';
import { CreditRegisterService } from '../../register/credit-register/service/credit-register.service';
import { CharacteristicConsultService } from '../../register/characteristic-consult/services/characteristic-consult.service';
import { B3DsRadioOption } from 'src/app/shared/b3-design-system-web/b3-ds-radio-group.component';
import { AddOthersBeneficiariesComponent } from './psa-add-beneficiary/psa-add-beneficiary.component';

@Component({
  standalone: false,
  selector: 'app-operation-withdrawal',
  templateUrl: './operation-withdrawal.component.html',
  styleUrls: ['./operation-withdrawal.component.scss']
})
export class OperationWithdrawalComponent implements OnInit, OnDestroy {

  formWithdrawal: FormGroup = new FormGroup({});
  @Input() formRegister!: FormGroup;
  clearFormNow: boolean = false;
  creditQuantity: number = 0;

  maskCpfOrCnpj: string = '';
  characterCount: number = 0;
  disabledWithdrawalMode: boolean = true;
  confirmation: boolean = false;
  isQuantityApproved: boolean = false;
  withdrawalAmountTotal: number = 0;
  titleModal = '';
  showWithdrawalResultModal = false;
  withdrawalResultModal: WithdrawalResultModalViewModel | null = null;
  showForeignEntityModal = false;
  foreignEntityIsForeigner = false;
  foreignEntityPreviousValue = false;
  showAttentionModal = false;
  private beneficiaryFormRef?: AddOthersBeneficiariesComponent;
  showcpfCnpjComitente: boolean = false;
  maskcpfCnpjComitente: string = '';
  comitenteFound = false;
  instrumentAvailableQuantity: number | null = null;
  instrumentTotalQuantity: number | null = null;
  loadingInstrument = false;
  readonly withdrawalModeOptions: B3DsRadioOption[] = [
    { value: '2', label: 'Aposentadoria' },
    { value: '1', label: 'Transferência de custódia para contraparte fora do B3' },
  ];

  private readonly destroy$ = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private operationService: OperationTransferenceService,
    private modalService: AppModalService,
    private router: Router,
    private creditRegisterService: CreditRegisterService,
    private characteristicConsultService: CharacteristicConsultService
  ) {
  }

  ngOnInit(): void {
    this.initFormWithdrawal();
    this.listenInstrumentQuantityChanges();

    this.formRegister.get('generalDetails')?.valueChanges
      .pipe(
        startWith(this.formRegister.get('generalDetails')?.value || ''),
        takeUntil(this.destroy$)
      )
      .subscribe(value => {
        this.characterCount = value ? value.length : 0;
      });

    this.formWithdrawal.get('holderCode')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        const accountType = value?.substring(6, 8);
        this.showcpfCnpjComitente = accountType === '10';

        if (this.showcpfCnpjComitente) {
          this.formWithdrawal.get('cpfCnpjComitente')?.setValidators([Validators.required, CpfCnpjValidator.validate]);
        } else {
          this.formWithdrawal.get('cpfCnpjComitente')?.clearValidators();
          this.formWithdrawal.get('cpfCnpjComitente')?.setValue('');
          this.maskcpfCnpjComitente = '';
          this.comitenteFound = false;
        }
        this.formWithdrawal.get('cpfCnpjComitente')?.updateValueAndValidity();
      });

    this.formWithdrawal.get('cpfCnpjComitente')?.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateComitenteFoundState();
      });

    this.formWithdrawal.get('cpfCnpjComitente')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.syncComitenteDocumentControlValue(value);
        const digitCount = extractDocumentCharacters(value).length;
        if (digitCount === 11 || digitCount === 14 || this.comitenteFound) {
          this.updateComitenteFoundState();
        }
      });

    this.formRegister.get('developerCnpj')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.maskCpfOrCnpj = '';
      });

    this.formWithdrawal.get('withdrawalMode')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        this.applyWithdrawalMode(value);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get showInstrumentQuantity(): boolean {
    return this.instrumentAvailableQuantity != null && this.instrumentTotalQuantity != null;
  }

  public initFormWithdrawal(): void {
    this.formWithdrawal = this.formBuilder.group({
      carbonCreditCode: ['', [Validators.required, Validators.maxLength(12), Validators.minLength(12)]],
      holderCode: ['', [Validators.required, Validators.maxLength(10), Validators.minLength(10), ValidateTwoValueInString('00','10', 6, 7)]],
      purchaserCode: ['', [Validators.required, Validators.maxLength(10), Validators.minLength(10), ValidateValueInString('40', 6, 7)]],
      creditQuantity: ['', [Validators.required, Validators.min(0), Validators.pattern('^[1-9][0-9]*$')]],
      withdrawalMode: ['', Validators.required],
      cpfCnpjComitente: ['', [Validators.required, CpfCnpjValidator.validate],
          [
            comitenteBackendValidator(
              this.creditRegisterService,
              () => this.formWithdrawal.get('holderCode')?.value
            )
          ]
      ]
    });
    this.formRegister = this.formBuilder.group({
      beneficiaries: [[]],
      developerCompanyNameAdd: [''],
      developerCnpjAdd: ['', [Validators.maxLength(18), CpfCnpjValidator.validate]],
      developerDescriptionAdd: [''],
      developerAmount: [0],
      developerForeigner: [false],
      description: [''],
    })
  }

  private listenInstrumentQuantityChanges(): void {
    this.formWithdrawal.get('carbonCreditCode')?.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(code => {
        const normalizedCode = (code ?? '').trim();

        if (normalizedCode.length !== 12) {
          this.resetInstrumentQuantity();
          return;
        }

        this.loadingInstrument = true;
        this.characteristicConsultService.getCarbonCreditById(normalizedCode)
          .pipe(
            catchError(() => of(null)),
            takeUntil(this.destroy$)
          )
          .subscribe(credit => {
            this.loadingInstrument = false;

            if (!credit) {
              this.resetInstrumentQuantity();
              return;
            }

            this.instrumentAvailableQuantity = credit.creditQuantity;
            this.instrumentTotalQuantity = credit.creditInitialQuantity;
          });
      });
  }

  private resetInstrumentQuantity(): void {
    this.instrumentAvailableQuantity = null;
    this.instrumentTotalQuantity = null;
    this.loadingInstrument = false;
  }

  public onCancel(): void {
    this.cleanForm();
  }

  public transferenceRegister(): void {
    let form = this.formWithdrawal.getRawValue();
    const modeValue = form.withdrawalMode;
    form.withdrawalMode = this.withdrawalModeOptions.find(option => option.value === modeValue)?.label ?? modeValue;
    form['withdrawals'] = this.formRegister.get('beneficiaries')?.getRawValue();
    this.operationService.transferenciaRetirada(form, CarbonOperationType.WITHDRAW).subscribe({
      next: (value: CarbonOperation) => {
        this.openWithdrawalResultModal(value);
      },
      error: (err: any) => {
        if (err.error?.[0] != null) {
          this.titleModal = err.error[0].message;
          this.openWithdrawalResultModal(err.error[0].message);
        } else {
          this.openWithdrawalResultModal(err);
        }
      },
    })
  }

  public treatCreditRecordSuccess(res: any): void {
    this.modalService.open(ModalConfirmationComponent, {
      modalTitleType: 'success',
      modalType: 'info',
      title: 'Retirada registrada com sucesso.',
      content: `Seu código de operação é ${res.operationCode}`,
      closeButton: true,
      size: 'mendium',
    });
    this.cleanForm();
  }

  public treatCreditRecordError(erro: any): void {
    this.modalService.open(ModalConfirmationComponent, {
      modalTitleType: 'error',
      modalType: 'info',
      title: 'Erro',
      content: 'Não foi possível realizar a retirada.',
      closeButton: true,
      size: 'mendium',
    });
  }

  public checkHasError(fieldName: string, errorCode?: string): boolean {
    let field = this.formWithdrawal.get(fieldName);
    if (field == null || !field.touched) {
      return false;
    }
    if (errorCode == null) {
      return field.invalid;
    }
    return field.hasError(errorCode);
  }

  public cleanForm(): void {
    this.formWithdrawal.reset();
    this.confirmation = false;
    this.comitenteFound = false;
    this.disabledWithdrawalMode = true;
    this.resetInstrumentQuantity();
    this.clearFormNow = true;
    setTimeout(() => {
      this.clearFormNow = false
    }, 500);
  }

  confirmationChange(event: Event): void {
    const customEvent = event as CustomEvent<{ selected?: boolean }>;
    this.confirmation = !!customEvent.detail?.selected;
  }

  openWithdrawalResultModal(data: CarbonOperation | string): void {
    this.withdrawalResultModal = buildWithdrawalResultModalViewModel(data, this.titleModal);
    this.showWithdrawalResultModal = true;
  }

  onWithdrawalResultModalAction(action: WithdrawalModalAction): void {
    this.showWithdrawalResultModal = false;
    this.titleModal = '';

    if (action === 'consult') {
      this.navigateToEmissionConsult();
    }

    if (action === 'clear') {
      this.cleanForm();
    }

    if (action === 'retry') {
      this.transferenceRegister();
    }
  }

  onForeignEntityModalRequest(
    event: { isForeigner: boolean; previousForeigner: boolean },
    beneficiaryForm: AddOthersBeneficiariesComponent,
  ): void {
    this.beneficiaryFormRef = beneficiaryForm;
    this.foreignEntityIsForeigner = event.isForeigner;
    this.foreignEntityPreviousValue = event.previousForeigner;
    this.showForeignEntityModal = true;
  }

  onForeignEntityModalClosed(value: boolean | null): void {
    this.showForeignEntityModal = false;
    this.beneficiaryFormRef?.applyForeignEntityResult(value, this.foreignEntityPreviousValue);
  }

  onAttentionModalRequest(): void {
    this.showAttentionModal = true;
  }

  onAttentionModalClosed(): void {
    this.showAttentionModal = false;
  }

  onWithdrawalModeChange(value: string): void {
    this.applyWithdrawalMode(value);
  }

  private applyWithdrawalMode(value: string | number | null | undefined): void {
    if (this.isRetirementModeValue(value)) {
      this.disabledWithdrawalMode = false;
      this.formRegister.get('beneficiaries')?.setValue([]);
      this.formRegister.get('developerCompanyNameAdd')?.setValue('');
      this.formRegister.get('developerCnpjAdd')?.setValue('');
      this.formRegister.get('developerDescriptionAdd')?.setValue('');
      this.formRegister.get('developerAmount')?.setValue('');
      this.formRegister.get('developerForeigner')?.setValue(false);
      return;
    }

    if (value === '1' || value === 1) {
      this.disabledWithdrawalMode = true;
      this.confirmation = false;
      this.formRegister.get('developerForeigner')?.setValue(false);
    }
  }

  private isRetirementModeValue(value: string | number | null | undefined): boolean {
    return value === '2' || value === 2;
  }

  onChangedccreditQuantity(data: Event | { value?: string | number }): void {
    const customEvent = data instanceof Event ? (data as CustomEvent<{ value?: string }>) : null;
    const value = customEvent?.detail?.value ?? (data as { value?: string | number })?.value;
    const beneficiaries = this.formRegister.get('beneficiaries')?.value;
    this.withdrawalAmountTotal = 0;
    beneficiaries.forEach((beneficiary: any) => {
      this.withdrawalAmountTotal += Number(beneficiary.withdrawalAmount);
    });
    this.creditQuantity = Number(value ?? 0);
  }

  onChangedApprovedQuantity(value: any) {
    this.isQuantityApproved = value;
  }

  onChangedAccumulatedAmount(value: any) {
    this.isQuantityApproved = true;
    this.creditQuantity = Number(value ?? 0);
    this.formWithdrawal.get('creditQuantity')?.setValue(value);
  }

  get beneficiariesArray(): FormArray {
    this.listenForeignerChanges();
    return this.formRegister.get('beneficiaries')?.value as FormArray;
  }

  get isRetireMode(): boolean {
    return this.isRetirementModeValue(this.formWithdrawal.get('withdrawalMode')?.value);
  }

  get showFormActions(): boolean {
    const mode = this.formWithdrawal.get('withdrawalMode')?.value;
    return mode === '1' || mode === '2' || mode === 1 || mode === 2;
  }

  /** Máscara livre durante digitação; formatação CPF/CNPJ ocorre no blur e via sync. */
  get comitenteDocumentMask(): 'text' {
    return 'text';
  }

  public onChangesComitenteCpfOrCnpj(): void {
    const control = this.formWithdrawal.get('cpfCnpjComitente');
    const next = formatCpfCnpjInputValue(control?.value, true);
    if (control?.value !== next) {
      control?.setValue(next);
    }
  }

  /** Durante a digitação: não formata CPF no 11º dígito (permite continuar para CNPJ). */
  private syncComitenteDocumentControlValue(raw: string | null | undefined): void {
    const control = this.formWithdrawal.get('cpfCnpjComitente');
    const alnum = extractDocumentCharacters(raw).slice(0, 14);

    if (!alnum) {
      if (control?.value) {
        control.setValue('', { emitEvent: false });
      }
      return;
    }

    let nextValue: string;
    if (alnum.length >= 12) {
      nextValue = formatCNPJProgressive(alnum);
    } else if (alnum.length === 11 && /[.\-/]/.test(String(raw ?? ''))) {
      nextValue = formatCpfCnpjInputValue(raw, true);
    } else {
      nextValue = alnum;
    }

    const currentAlnum = extractDocumentCharacters(control?.value);
    const isCompleteDocument = alnum.length === 11 || alnum.length === 14;

    if (currentAlnum === alnum && String(control?.value ?? '') === nextValue) {
      return;
    }

    control?.setValue(nextValue, { emitEvent: isCompleteDocument });
  }

  private updateComitenteFoundState(): void {
    const control = this.formWithdrawal.get('cpfCnpjComitente');

    if (!control || !this.showcpfCnpjComitente) {
      this.comitenteFound = false;
      return;
    }

    const digits = String(control.value ?? '').replace(/\D/g, '');
    const hasFullDocument = digits.length === 11 || digits.length === 14;

    this.comitenteFound = hasFullDocument
      && control.status === 'VALID'
      && control.valid
      && !control.hasError('comitenteInvalido')
      && !control.hasError('backendError');
  }

  private listenForeignerChanges(): void {
    const foreignerCtrl = this.formRegister.get('developerForeigner');
    const cnpjCtrl = this.formRegister.get('developerCnpjAdd');

    foreignerCtrl?.valueChanges.subscribe((isForeigner: boolean) => {
      if (!cnpjCtrl) return;

      if (isForeigner) {
        cnpjCtrl.setValidators([Validators.maxLength(18)]);
      } else {
        cnpjCtrl.setValidators([Validators.maxLength(18), CpfCnpjValidator.validate]);
      }

      cnpjCtrl.updateValueAndValidity();
    });
  }

  navigateToEmissionConsult() {
    this.router.navigate(['/consulta-operacoes']);
  }
}
